/**
 * NOS-217: footer logo that stays visible on ANY footer background.
 *
 * Shared by both footer surfaces (builder canvas FooterSection and
 * public/preview PublicFooter) so the treatment can never drift.
 *
 * Two mechanisms, selectable via footer_content.logo_treatment (footer panel):
 *
 * - "knockout" (default): when the logo would collide with the footer
 *   background, render it as a solid silhouette via CSS filter — white on
 *   dark backgrounds, black on light ones. Clean, no box; loses brand color.
 *   Applies only when sampling CONFIRMED transparency — an opaque image
 *   (JPEG, flattened PNG) would have its whole rectangle flooded by the
 *   filter, so it falls back to the chip.
 * - "chip": the full-color logo on a small contrasting chip.
 *
 * The logo is sampled client-side via canvas (useImageSample, cached per
 * URL); when sampling is impossible (external SVG without CORS — NOS-179
 * excludes SVG from rehosting — or no canvas) the safe default applies:
 * chip on dark backgrounds, bare on light ones, because imported org logos
 * are overwhelmingly dark wordmarks.
 *
 * A user-set footer logo override (footer_content.logo_url, PR #240) is
 * always rendered untouched — never chipped, never filtered.
 */
import { needsLogoChip } from "@/lib/colorContrast"
import { useImageSample } from "@/hooks/useImageLuminance"
import type { FooterLogoTreatment } from "@/lib/footerContent"

export interface FooterLogoProps {
  src: string
  alt: string
  /** Rendered logo height in px (already logo_scale-adjusted by the caller). */
  height: number
  /** True when src is the user-set footer_content.logo_url override. */
  isUserOverride: boolean
  /** Resolved footer background classification (footerContent resolver). */
  backgroundIsDark: boolean
  /** Resolved footer_content.logo_treatment (default "knockout"). */
  treatment: FooterLogoTreatment
}

export function FooterLogo({
  src,
  alt,
  height,
  isUserOverride,
  backgroundIsDark,
  treatment,
}: FooterLogoProps) {
  // Overrides are never treated — skip sampling entirely.
  const sample = useImageSample(isUserOverride ? null : src)
  const collides = needsLogoChip(backgroundIsDark, sample.luminance, isUserOverride)
  // Knockout requires CONFIRMED transparency; opaque/unknown falls back to chip.
  const knockout = collides && treatment === "knockout" && sample.hasAlpha === true

  const img = (
    <img
      src={src}
      alt={alt}
      data-testid={knockout ? "footer-logo-knockout" : undefined}
      style={{
        height,
        width: "auto",
        objectFit: "contain",
        display: "block",
        ...(knockout
          ? {
              filter: backgroundIsDark
                ? "brightness(0) invert(1)"
                : "brightness(0)",
            }
          : {}),
      }}
    />
  )

  if (!collides || knockout) {
    return <div style={{ marginBottom: 12 }}>{img}</div>
  }

  return (
    <div style={{ marginBottom: 12 }}>
      <div
        data-testid="footer-logo-chip"
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "6px 10px",
          borderRadius: 8,
          background: backgroundIsDark ? "rgba(255,255,255,0.95)" : "rgba(15,15,20,0.92)",
        }}
      >
        {img}
      </div>
    </div>
  )
}
